import { Context, Controller } from '@hotwired/stimulus';

type DashboardRepresentation = {
  _links: {
    entrypoint: {
      href: string;
    };
    my_assigned_tasks: {
      href: string;
    };
    my_newsletters: {
      href: string;
    };
    my_profile: {
      href: string;
    };
    my_second_factors: {
      href: string;
    };
    my_subscriptions: {
      href: string;
    };
    logout: {
      href: string;
    };
    sites: {
      href: string;
    };
    next_task?: {
      href: string;
      title: string;
      opened_at: string;
      updated_at: string;
    };
  };
  inboxes: {
    href: string;
    name: string;
    count_assigned: number;
    count_open_and_unassigned: number;

    newest_task?: {
      href: string;
      title: string;
      opened_at: string;
    };
  }[];
  employees: {
    count_due_elearning: number;
    count_due_training: number;
    count_due_checklists: number;
    dashboards: {
      href: string;
      name: string;
    }[];
  };
  security: {
    has_no_display_name: boolean;
    has_no_profile_picture: boolean;
    has_no_2fa: boolean;
    has_no_2fa_mandate: boolean;
    count_otp_backups_keys: number;
  };
  subscriptions: {
    has_subscribed_to_employee_tasks: boolean;
    has_digest: boolean;
    email: string;
  };
};

export default class DashboardController extends Controller {
  public static targets = [
    'inbox',
    'oneThing',
    'inboxNewestTask',
    'inboxNoNewestTask',
  ];

  public static values = {
    dataUrl: String,
  };

  declare private dataUrlValue: string;

  declare private oneThingTarget: HTMLElement;
  declare private inboxTargets: HTMLElement[];
  declare private inboxNewestTaskTarget: HTMLTemplateElement;
  declare private inboxNoNewestTaskTarget: HTMLTemplateElement;

  private abortController!: AbortController;

  public constructor(context: Context) {
    super(context);
  }

  public inboxTargetConnected(_element: HTMLElement) {}

  public inboxTargetDisconnected(_element: HTMLElement) {}

  public connect(): void {
    super.connect();

    this.abortController = new AbortController();

    void fetch(this.dataUrlValue, {
      headers: { accept: 'application/vnd.opus-safety.dashboard.v1+json' },
      signal: this.abortController.signal,
      credentials: 'same-origin',
    })
      .then((response) => {
        const [contentType] = (
          response.headers.get('content-type') || ''
        ).split(';');

        if (
          contentType.includes('application/vnd.opus-safety.dashboard') &&
          contentType.endsWith('+json')
        ) {
          return response.json();
        }

        throw new Error(
          `Expected 'application/vnd.opus-safety.dashboard.v1+json', actual: ${contentType || '(none)'}`,
        );
      })
      .then((response) => response as DashboardRepresentation)
      .then((representation) => this.inflate(representation));
  }

  public disconnect(): void {
    super.disconnect();

    if (this.abortController) {
      this.abortController.abort(new Error('DashboardController#disconnect'));
    }
  }

  private inflate(data: DashboardRepresentation) {
    for (const inboxData of data.inboxes) {
      const inboxElem = this.inboxTargets.find(
        (target) =>
          target.getAttribute('data-dashboard-inbox-site-url-value') ===
          inboxData.href,
      );
      if (!inboxElem) {
        continue;
      }

      const newestTaskElem = inboxElem.querySelector(
        '[data-dashboard-inbox-target="inbox-newest-task"]',
      )!;

      while (newestTaskElem.lastChild) {
        newestTaskElem.lastChild.remove();
      }

      if (inboxData.newest_task) {
        const copy = this.inboxNewestTaskTarget.cloneNode(
          true,
        ) as typeof this.inboxNewestTaskTarget;

        copy.innerHTML = copy.innerHTML
          .replace(/:title/g, inboxData.newest_task.title)
          .replace(/:datetime/g, inboxData.newest_task.opened_at);

        const inflatedHTML = copy.content.firstElementChild!.innerHTML;
        newestTaskElem.innerHTML = inflatedHTML;
      } else {
        const copy = this.inboxNoNewestTaskTarget.cloneNode(
          true,
        ) as typeof this.inboxNewestTaskTarget;
        const inflatedHTML = copy.content.firstElementChild!.innerHTML;
        newestTaskElem.innerHTML = inflatedHTML;
      }

      const countsElem = inboxElem.querySelector(
        '[data-dashboard-inbox-target="inbox-counts-text"]',
      )!;
      countsElem.textContent = `my assigned: ${inboxData.count_assigned}, open & unassigned: ${inboxData.count_open_and_unassigned}`;

      const countsAssignedElem = inboxElem.querySelector(
        '[data-dashboard-inbox-target="inbox-counts-assigned"]',
      )!;
      countsAssignedElem.textContent = `my assigned: ${inboxData.count_assigned}`;

      const countsOpenElem = inboxElem.querySelector(
        '[data-dashboard-inbox-target="inbox-counts-open"]',
      )!;
      countsOpenElem.textContent = `open & unassigned: ${inboxData.count_open_and_unassigned}`;
    }

    if (!data._links.next_task?.href) {
      return;
    }

    void fetch(data._links.next_task.href, {
      headers: { accept: 'text/vnd.opus-safety.dashboard.cell-html' },
      signal: this.abortController.signal,
      credentials: 'same-origin',
    })
      .then((response) => {
        const [contentType] = (
          response.headers.get('content-type') || ''
        ).split(';');

        if (
          contentType === 'text/vnd.opus-safety.dashboard.cell-html' ||
          contentType === 'text/html'
        ) {
          return response.text();
        }

        throw new Error(
          `Expected 'text/vnd.opus-safety.dashboard.cell-html', actual: ${contentType || '(none)'}`,
        );
      })
      .then((representation) => this.inflateNextTask(representation));
  }

  private inflateNextTask(html: string) {
    this.oneThingTarget.innerHTML = html;
  }
}

/*
<template>
  my assigned: <%= tasks.my_active_tasks.count %>, open &amp; unassigned: <%= tasks.all_open_tasks.count %>

  my assigned: <%= tasks.my_active_tasks.count %>

  open &amp; unassigned: <%= tasks.all_open_tasks.count %>

  <section class="mt-4 lg:mt-6 lg:mb-8">
    <h2 class="text-sm/4 font-semibold text-theme-accent-700 dark:text-theme-accent-500">If you only do one thing today</h2>

    <div class="mt-2 overflow-hidden rounded-lg shadow-sm px-4 sm:px-0">
      <section data-component="todos/list" class="forced-colors:border todos-list ">
        <ul>
          <li data-component="generic/card_list_item" data-controller="accessible-card-link" class="group card-list-item --interactable todo-card cursor-pointer">
            <article>
              <div class="card-list-item__contents print:!pl-4">
                <div class="card-list-item__contents-inner">
                  <div class="truncate">
                    <h3 class="card-list-item__title text-red-600 dark:text-red-400">
                      <figure class="card-list-item__severity text-red-600 dark:text-red-400" style="width: 1.6em; font-size: 0.875rem; line-height: 1.25rem;" title="Severity: major">
                        <svg data-component="todos/severity_icon" class="card-list-item__severity-icon" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true" title="major"><path fill-rule="evenodd" clip-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"></path></svg>
                        <figcaption class="sr-only">Severity: major</figcaption>
                      </figure>

                      <a class="focus:outline-hidden truncate" data-accessible-card-link-target="link" href="https://cloud.opus-safety.co.uk/todos/5c47726f-d59d-4b38-aa6a-fc0a6b886f4a">
                        <span class="absolute inset-0" aria-hidden="true" data-accessible-card-link-target="extension" hidden=""></span>

                        <span class="sr-only">Unresolved: </span>Call Point Check Action
                      </a>
                    </h3>

                    <p class="card-list-item__description ml-5 pl-0.5">
                      <time aria-hidden="true" class="hidden print:block mr-2" datetime="2025-05-14T17:58:39Z">2025-05-14 17:58:39 UTC</time>
                      <span class="mr-2 truncate print:hidden">
                        <span class="sr-only sm:not-sr-only">opened on&nbsp;</span><time datetime="2025-05-14T17:58:39Z" data-controller="time" data-time-format-value="datetime">14 May 2025, 19:58:39</time>&nbsp;by&nbsp;<em>System</em>
                      </span>

                      <span class="flex flex-wrap items-center mr-2">
                        <svg xmlns="http://www.w3.org/2000/svg" class="todo-card__meta" viewBox="0 0 20 20" fill="currentColor">
                          <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clip-rule="evenodd"></path>
                        </svg> updated&nbsp;<time datetime="2025-07-22T13:04:04Z" data-controller="time" data-time-format-value="relative" class="print:hidden" timeago-id="6">about 1 month ago</time>
                        <time aria-hidden="true" class="hidden print:block" datetime="2025-07-22T13:04:04Z">
                          2025-07-22 13:04:04 UTC
                        </time>
                      </span>
                    </p>

                    <div class="ml-4 pl-0.5">
                      <ul class="relative flex flex-wrap mb-1" data-component-section="todos/list_item#meta">
                        <li class="mt-1 mr-1 max-w-full opacity-80">
                          <button type="button" data-component="todos/label"
                            data-action="
                              focus->remote-popover#onReferenceUp
                              blur->remote-popover#onReferenceDownNextFrame
                              mouseenter->remote-popover#onDelayedReferenceUp
                              mouseleave->remote-popover#onDelayedReferenceDown
                            "
                            data-controller="remote-popover"
                            data-remote-popover-delay-time="150"
                            data-remote-popover-url-value="https://cloud.opus-safety.co.uk/labels/c3lzdGVtL2NvcnJlY3RpdmVfYWN0aW9u.dialog?context=8526d68a-f078-4144-ab4e-3a3ede54ae9c"
                            data-remote-popover-align-value="start"
                            data-remote-popover-side-value="outside-top"
                            data-remote-popover-anchor-offset-value="12"
                            data-remote-popover-alignment-offset-value="-2"
                            data-remote-popover-modal-value="[data-component='generic/card_list_item']"
                            class="
                              inline-flex items-start mr-1 px-1.5 py-0.5 rounded-md
                              text-xs font-medium break-all max-w-full
                              space-x-1 font-mono
                              group
                              transition
                              hover:shadow-xs focus:shadow-xs active:shadow-none
                              disabled:shadow-none disabled:hover:shadow-none disabled:focus:shadow-none
                              truncate
                              bg-gray-100 text-gray-800 border-transparent border expanded:border-gray-600 dark:bg-transparent dark:border-gray-400 dark:text-gray-100
                            "
                            tabindex="0">
                            <span class="shrink-0 font-light whitespace-nowrap" translate="no">system</span>
                            <span class="shrink-0 text-gray-400 dark:text-gray-300">/</span>
                            <span class="shrink" translate="no">corrective_action</span>
                          </button>
                        </li>
                      </ul>

                    </div>
                  </div>
                </div>

                  <div class="card-list-item__assigned">
                          <div data-component="users/avatar" class="relative rounded-full transition group-focus:ring-2 group-focus:theme-primary-dark shrink-0 pointer" data-controller="remote-popover" data-action="focus-&gt;remote-popover#onReferenceUp blur-&gt;remote-popover#onReferenceDownNextFrame mouseenter-&gt;remote-popover#onDelayedReferenceUp mouseleave-&gt;remote-popover#onDelayedReferenceDown" data-remote-popover-url-value="https://cloud.opus-safety.co.uk/context/for/users/BAh7CEkiCGdpZAY6BkVUSSJGZ2lkOi8va2Fib29tL1VzZXIvOWYwOWQ4MTQtYWNlZi00NjBkLWFmNWYtZDNlZDczZGYyMGI1P2V4cGlyZXNfaW4GOwBUSSIMcHVycG9zZQY7AFQ6DGNvbnRleHRJIg9leHBpcmVzX2F0BjsAVDA=--13ebf029c772174225dd7819d47b84f3509b6b73.dialog?context=BAh7CEkiCGdpZAY6BkVUSSJGZ2lkOi8va2Fib29tL1NpdGUvODUyNmQ2OGEtZjA3OC00MTQ0LWFiNGUtM2EzZWRlNTRhZTljP2V4cGlyZXNfaW4GOwBUSSIMcHVycG9zZQY7AFQ6DGNvbnRleHRJIg9leHBpcmVzX2F0BjsAVDA%3D--d1ff55947863a8ed73ca343d2f52c0a698046664&amp;current_site=8526d68a-f078-4144-ab4e-3a3ede54ae9c" data-remote-popover-align-value="start" data-remote-popover-side-value="outside-top" data-remote-popover-anchor-offset-value="12" data-remote-popover-alignment-offset-value="-2" tabindex="0"><img data-component="generic/avatar" class="object-center object-cover dark:bg-black/20 aspect-square print-color-exact rounded-full bg-gray-100 dark:bg-gray-900 todo-card__assigned" width="40" height="40" alt="This ticket is assigned to Derk-Jan (Opus)" src="https://cloud.opus-safety.co.uk/rails/active_storage/representations/redirect/eyJfcmFpbHMiOnsibWVzc2FnZSI6IkJBaEpJaWsyTURWak9UZGpNUzAxTlRWbUxUUmtabUl0T0RBNVlTMW1ZbVEyTTJSbFpUTXlPV1FHT2daRlZBPT0iLCJleHAiOm51bGwsInB1ciI6ImJsb2JfaWQifX0=--5e37d362a768d987e113f72fab2fc5cc329b23ff/eyJfcmFpbHMiOnsibWVzc2FnZSI6IkJBaDdCem9MWm05eWJXRjBTU0lJYW5CbkJqb0dSVlE2RTNKbGMybDZaVjkwYjE5bWFXeHNXd2RwVldsViIsImV4cCI6bnVsbCwicHVyIjoidmFyaWF0aW9uIn19--131482e70aa050d8ca548ee3d5fb90f18001bd08/profile-4k.jpg"><span class="page__header-icon-overlay rounded-full" aria-hidden="true"></span></div>

                  </div>

                  <div class="card-list-item__interactable print:hidden">
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                      <path fill-rule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clip-rule="evenodd"></path>
                    </svg>
                  </div>
              </div>

            </article>

          </li>

        </ul>
      </section>

    </div>
  </section>
</template>
*/
